import useUser from '~/composables/useUser'
import {
  EquipmentTypeEnum,
  ProjectRead,
  SwitchingCategoryTypeEnum,
  ThemeEnum,
} from '@gridcon-energy/schaltungstool-backend'
import { computed, inject, ref } from 'vue'
import { useTheme } from 'vuetify'
import type { AppSettings } from '~/composables/app-settings/helpers'
import { createDefaultSettings, loadUserSettings } from '~/composables/app-settings/helpers'
import { AppSettingsKey } from '~/lib/symbols'
import { useNuxtApp } from '#app'
import { useEventListener } from '@vueuse/core'
import type { GanttCSSProperties } from '~/lib/gantt'
import {
  OtherCSSVariables,
  PremiseViolationDynamicCSSVariables,
  PremiseViolationStaticCSSVariables,
  SBRestrictionCSSVariables,
  TbtDailyCSSVariables,
} from '~/lib/gantt'
import { ColoringContextEnum, VoltageLevelEnum } from '~/composables/coloring/constants'
import {
  CSSVariableCategory,
  CSSVariableEquipmentStatus,
  CSSVariableEquipmentType,
  CSSVariableNetworkState,
  CSSVariableOfflineRange,
  CSSVariableProject,
  CSSVariableTextOpacityCategory,
  CSSVariableTextOpacityEquipmentStatus,
  CSSVariableTextOpacityEquipmentType,
  CSSVariableTextOpacityNetworkState,
  CSSVariableTextOpacityOfflineRange,
  CSSVariableTextOpacityProject,
  CSSVariableTextOpacityVoltage,
  CSSVariableVoltage,
} from '~/composables/coloring/helpers'
import { OfflineStatusEnum } from '~/lib/switchings'
import { EquipmentNetworkStateEnum, EquipmentStatusEnum } from '~/lib/equipments'
import type { GanttSettings } from '~/composables/gantt/useGanttSettings'
import type { ActivityColoringCSSProperties } from '~/lib/activity'
import { extractOpacityFromHex } from '~/helper/colors'
import type { PartialDeep } from 'type-fest'

// Dependency for settings
const loadedProjects = ref<ProjectRead[]>([])

function usesDarkTheme(val: ThemeEnum) {
  return val === ThemeEnum.Dark || (val === ThemeEnum.Auto && window.matchMedia('(prefers-color-scheme: dark)').matches)
}

export type AppSettingsComposable = ReturnType<typeof provideAppSettings>
/**
 * Depending on User and loaded Projects
 */
export default function provideAppSettings() {
  const { vueApp } = useNuxtApp()
  const theme = useTheme()
  const { user, updateUser } = useUser()

  function applyTheme(themeProvided: ThemeEnum | undefined | null) {
    const usesDark = usesDarkTheme(themeProvided ?? ThemeEnum.Auto)
    const newTheme = usesDark ? ThemeEnum.Dark : ThemeEnum.Light
    if (theme.name.value !== newTheme) {
      console.log(`Theme changed from ${theme.name.value} to ${newTheme}`)
      theme.change(newTheme)
    }
  }

  const settingsDefault = () => createDefaultSettings(loadedProjects.value) // cannot be computed prop and always created on-the-fly
  const settings = computed(() => loadUserSettings(user.value, settingsDefault()))

  /**
   * Setting OS theme preference
   */
  const ThemePreference = ref<typeof ThemeEnum.Light | typeof ThemeEnum.Dark>(ThemeEnum.Dark)
  const themeQuery = window.matchMedia('(prefers-color-scheme: light)')
  if (themeQuery.matches) {
    ThemePreference.value = ThemeEnum.Light
  }

  /**
   * Handle change of OS theme preference change
   */
  useEventListener(themeQuery, 'change', (ev: MediaQueryListEvent) => {
    ThemePreference.value = ev.matches ? ThemeEnum.Light : ThemeEnum.Dark
    if (settings.value.theme === ThemeEnum.Auto) {
      applyTheme(ThemePreference.value)
    }
  })

  async function updateSettings(settingsUpdate: PartialDeep<AppSettings>, mergeSettings = true) {
    if (!user.value) {
      return null
    }
    return await updateUser(settingsUpdate, { mergeSettings })
  }

  const useDrawerNavigation = computed({
    get: () => settings.value.settings.useDrawerNavigation,
    set: (newValue) => updateSettings({ settings: { useDrawerNavigation: newValue } }),
  })

  const drawerExpanded = computed({
    get: () => settings.value.settings.drawerExpanded,
    set: (newValue) => updateSettings({ settings: { drawerExpanded: newValue } }),
  })

  const appSettings = {
    applyTheme: applyTheme,
    settings: settings,
    settingsDefault,
    useDrawerNavigation,
    drawerExpanded,
    loadedProjects,
    ThemePreference,
  }

  // Use app provide
  vueApp.provide(AppSettingsKey, appSettings)
  return appSettings
}

export function injectAppSettings() {
  const appSettings = inject(AppSettingsKey)
  if (!appSettings) {
    throw new Error('No app settings provided')
  }
  return appSettings
}

/**
 * CSS Coloring defined by user for
 * - Switching Category
 * - Project ids
 * - Voltages
 */
export function generateActivityCSSVariables(settings: AppSettings): ActivityColoringCSSProperties {
  const coloring = {} as ActivityColoringCSSProperties

  for (const ctx of Object.values(ColoringContextEnum)) {
    /** Switching Category */
    for (const switchingCategory of Object.values(SwitchingCategoryTypeEnum)) {
      const cssVariableCategory = CSSVariableCategory(ctx, switchingCategory)
      coloring[cssVariableCategory] = settings.design.coloring[ctx].category[switchingCategory].color

      // Text Opacity for SVG - extracted from color
      if (ctx === ColoringContextEnum.SVG) {
        const cssVariableTextOpacity = CSSVariableTextOpacityCategory(switchingCategory)
        const opacity = extractOpacityFromHex(settings.design.coloring[ctx].category[switchingCategory].color)
        coloring[cssVariableTextOpacity] = opacity.toString()
      }
    }

    /** Projects */
    for (const project of loadedProjects.value) {
      const cssVariableCategory = CSSVariableProject(ctx, project.id)
      if (project.id in settings.design.coloring[ctx].project) {
        coloring[cssVariableCategory] = settings.design.coloring[ctx].project[project.id].color

        // Text Opacity for SVG - extracted from color
        if (ctx === ColoringContextEnum.SVG) {
          const cssVariableTextOpacity = CSSVariableTextOpacityProject(project.id)
          const opacity = extractOpacityFromHex(settings.design.coloring[ctx].project[project.id].color)
          coloring[cssVariableTextOpacity] = opacity.toString()
        }
      } else {
        coloring[cssVariableCategory] = settings.design.coloring[ctx].project['default'].color

        // Text Opacity for SVG - extracted from color
        if (ctx === ColoringContextEnum.SVG) {
          const cssVariableTextOpacity = CSSVariableTextOpacityProject(project.id)
          const opacity = extractOpacityFromHex(settings.design.coloring[ctx].project['default'].color)
          coloring[cssVariableTextOpacity] = opacity.toString()
        }
      }
    }
    // project default
    const defaultProject = CSSVariableProject(ctx, 'default')
    coloring[defaultProject] = settings.design.coloring[ctx].project['default'].color

    // Text Opacity for default project (SVG) - extracted from color
    if (ctx === ColoringContextEnum.SVG) {
      const cssVariableTextOpacity = CSSVariableTextOpacityProject('default')
      const opacity = extractOpacityFromHex(settings.design.coloring[ctx].project['default'].color)
      coloring[cssVariableTextOpacity] = opacity.toString()
    }

    /** Voltage */
    for (const voltageLevel of Object.values(VoltageLevelEnum)) {
      const cssVariableVoltage = CSSVariableVoltage(ctx, voltageLevel)
      coloring[cssVariableVoltage] = settings.design.coloring[ctx].voltage[voltageLevel].color

      // Text Opacity for SVG - extracted from color
      if (ctx === ColoringContextEnum.SVG) {
        const cssVariableTextOpacity = CSSVariableTextOpacityVoltage(voltageLevel)
        const opacity = extractOpacityFromHex(settings.design.coloring[ctx].voltage[voltageLevel].color)
        coloring[cssVariableTextOpacity] = opacity.toString()
      }
    }

    /** Equipment Type */
    for (const equipmentType of Object.values(EquipmentTypeEnum)) {
      const cssVariableEquipmentType = CSSVariableEquipmentType(ctx, equipmentType)
      coloring[cssVariableEquipmentType] = settings.design.coloring[ctx].equipment_type[equipmentType].color

      // Text Opacity for SVG - extracted from color
      if (ctx === ColoringContextEnum.SVG) {
        const cssVariableTextOpacity = CSSVariableTextOpacityEquipmentType(equipmentType)
        const opacity = extractOpacityFromHex(settings.design.coloring[ctx].equipment_type[equipmentType].color)
        coloring[cssVariableTextOpacity] = opacity.toString()
      }
    }

    /** Offline Range */
    const pendingActivation = CSSVariableOfflineRange(ctx, OfflineStatusEnum.pendingActivation)
    coloring[pendingActivation] = settings.design.coloringOfflineRange[ctx].pendingActivation

    const deactivated = CSSVariableOfflineRange(ctx, OfflineStatusEnum.deactivated)
    coloring[deactivated] = settings.design.coloringOfflineRange[ctx].deactivated

    const temporary = CSSVariableOfflineRange(ctx, OfflineStatusEnum.temporary)
    coloring[temporary] = settings.design.coloringOfflineRange[ctx].temporary

    // Text Opacity for SVG - extracted from color
    if (ctx === ColoringContextEnum.SVG) {
      for (const offlineStatus of Object.values(OfflineStatusEnum)) {
        const cssVariableTextOpacity = CSSVariableTextOpacityOfflineRange(offlineStatus)
        const opacity = extractOpacityFromHex(settings.design.coloringOfflineRange[ctx][offlineStatus])
        coloring[cssVariableTextOpacity] = opacity.toString()
      }
    }
  }

  // Only SVG
  /** Network State **/
  for (const networkState of Object.values(EquipmentNetworkStateEnum)) {
    const cssVariableNetworkState = CSSVariableNetworkState(networkState)
    coloring[cssVariableNetworkState] = settings.design.coloringSVGNetworkState[networkState].color

    // Text Opacity - extracted from color
    const cssVariableTextOpacity = CSSVariableTextOpacityNetworkState(networkState)
    const opacity = extractOpacityFromHex(settings.design.coloringSVGNetworkState[networkState].color)
    coloring[cssVariableTextOpacity] = opacity.toString()
  }

  /** Equipment Status **/
  for (const status of Object.values(EquipmentStatusEnum)) {
    const cssVariableEquipmentStatus = CSSVariableEquipmentStatus(status)
    coloring[cssVariableEquipmentStatus] = settings.design.coloringEquipmentStatus[status].color

    // Text Opacity - extracted from color
    const cssVariableTextOpacity = CSSVariableTextOpacityEquipmentStatus(status)
    const opacity = extractOpacityFromHex(settings.design.coloringEquipmentStatus[status].color)
    coloring[cssVariableTextOpacity] = opacity.toString()
  }

  return coloring
}

/**
 * CSS for Gantt Chart and Legend
 */
export function generateGanttCSSVariables(settings: AppSettings, ganttSettings: GanttSettings): GanttCSSProperties {
  const colorPremisePrevious = settings.design.switchingBlockRestrictionPrevious
  const colorParallelForbidden = settings.design.switchingBlockRestrictionParallelForbidden

  return {
    // Restrictions - Schaltungsblockprämissen
    [SBRestrictionCSSVariables.parallel]: '',
    [SBRestrictionCSSVariables.parallel_forbidden]: colorParallelForbidden,
    [SBRestrictionCSSVariables.previous]: colorPremisePrevious,

    // SwitchingPremiseDynamic - dynamische Schaltungsprämissen
    [PremiseViolationDynamicCSSVariables.offline]: settings.design.switchingPremiseDynamicOffline,
    [PremiseViolationDynamicCSSVariables.protective]: settings.design.switchingPremiseDynamicProtective,
    [PremiseViolationDynamicCSSVariables.online]: settings.design.switchingPremiseDynamicOnline,

    // SwitchingPremiseStatic - statische Schaltungsprämissen
    [PremiseViolationStaticCSSVariables.consecutive_activated]:
      settings.design.switchingPremiseStaticConsecutiveActivated,
    [PremiseViolationStaticCSSVariables.consecutive_deactivated]:
      settings.design.switchingPremiseStaticConsecutiveDeactivated,

    // Other
    [OtherCSSVariables.rowExpandedBackground]: `rgba(136, 136, 136, 0.1)`,
    [OtherCSSVariables.gridLineColor]: `rgba(136, 136, 136, 0.5)`,

    [OtherCSSVariables.barMainHeight]: `${(settings.design.periodOptimized.height / 100) * ganttSettings.inStorage.value.rowHeight}px`,
    [OtherCSSVariables.barMainOpacity]: `${settings.design.periodOptimized.opacity / 100}`,

    [OtherCSSVariables.barBgHeight]: `${(settings.design.periodPossible.height / 100) * ganttSettings.inStorage.value.rowHeight}px`,
    [OtherCSSVariables.barBgOpacity]: `${settings.design.periodPossible.opacity / 100}`,
    [OtherCSSVariables.barBgColorOverlap]: '#0000FF',
    [OtherCSSVariables.barOverlapOpacity]: `0.3`,

    [OtherCSSVariables.barTimeFrameOriginalColor]: `${settings.design.periodInitial.color}`,
    [OtherCSSVariables.barTimeFrameWidth]: `${settings.design.periodInitial.width / 100}`,

    [OtherCSSVariables.barLimitViolationColor]: settings.design.switchingLimitViolation,

    // TBT Daily stripe pattern
    [TbtDailyCSSVariables.color]: settings.design.switchingInfoTbtDaily.color,
    [TbtDailyCSSVariables.spacing]: `${settings.design.switchingInfoTbtDaily.spacing}px`,
    [TbtDailyCSSVariables.angle]: `${settings.design.switchingInfoTbtDaily.angle}deg`,
  }
}
